import classNames from 'classnames';
import type { FC } from 'react';
import React, { memo, useEffect } from 'react';

import { Breadcrumb } from '@sravni/react-design-system/lib/Breadcrumb';
import { Html, Reviews } from '@sravni/react-seo';
import { useStyles } from '@sravni/react-utils';
import { getOrganizationHelpers } from '@sravni/ugc/lib/organizations/helpers';
import { makeServiceReviewsPageLink } from '@sravni/ugc/lib/organizations/linkMakers';

import type { ReviewsListParams } from '../../__generated__/reviews/reviewsApiTypes';
import { SravniReviewsEnumsReviewRateStatus } from '../../__generated__/reviews/reviewsApiTypes';
import {
  OrganizationTypeToOrganizationReviewsMap,
  PAGE_ROUTE_TO_ORGANIZATION_TYPE_MAP,
} from '../../constants/organizationType';
import { useIsWlPage } from '../../hooks/useIsWlPage';
import { useMetadataByPath } from '../../hooks/useMetadataByPath';
import { useFilters } from '../../store/hooks/filters';
import { useAppDispatch } from '../../store/hooks/redux';
import { useReviews } from '../../store/hooks/reviews';
import { useFingerprint } from '../../store/hooks/users';
import { fetchReviews } from '../../store/reducers/reviews';

import styles from './seo-blocks.module.scss';

export const SeoBlocks: FC = memo(() => {
  const { organizationType, service } = useFilters();
  const metadata = useMetadataByPath();
  const organizationHelper = getOrganizationHelpers(organizationType);

  const { breadcrumbs = [], contentBlocks = [] } = metadata ?? {};
  const dispatch = useAppDispatch();
  const reviews = useReviews();
  const fp = useFingerprint();
  const isWl = useIsWlPage();
  const cx = useStyles(styles);

  const linkToReviews = Object.entries(PAGE_ROUTE_TO_ORGANIZATION_TYPE_MAP).find(
    ([_, type]) => type === organizationType,
  )?.[0];

  // @ts-ignore
  const htmlText = contentBlocks.find((block) => block.type === 'html')?.text;

  useEffect(() => {
    if (reviews) return;

    dispatch(
      fetchReviews({
        ReviewObjectType: organizationHelper.reviewService,
        NewIds: true,
        RateStatus: SravniReviewsEnumsReviewRateStatus.RateApproved,
        Tag: service?.key as ReviewsListParams['Tag'],
        fingerPrint: fp,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationType, reviews]);

  let allReviewsLink = `/${linkToReviews}/otzyvy/`;

  if (service?.key) {
    allReviewsLink = makeServiceReviewsPageLink(service.alias);
  }

  return (
    <>
      {reviews?.length > 0 ? (
        <Reviews
          heading="Отзывы"
          reviews={reviews}
          productType={OrganizationTypeToOrganizationReviewsMap[organizationType]}
          allReviewsLink={allReviewsLink}
          gradient
          className={cx('h-mt-32', 'carousel', { 'h-mb-20': isWl })}
        />
      ) : null}
      {htmlText && <Html text={htmlText} className="h-mt-40" />}
      {!!breadcrumbs?.length && (
        <div className={classNames(styles.breadcrumbs, 'h-mt-36')}>
          <Breadcrumb>
            {breadcrumbs.map(({ url, title }) => (
              <Breadcrumb.Item href={url} key={url}>
                {title}
              </Breadcrumb.Item>
            ))}
          </Breadcrumb>
        </div>
      )}
    </>
  );
});
